import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import { useAuth } from 'auth/AuthContext';
import TooltipComponent from './common/TooltipComponent';

const NavTabs = ({ tabs = [], checkPermissions = false, orientation = 'vertical' }) => {
  const { user } = useAuth();
  const userPermissions = user?.permissions || [];

  const isHorizontal = orientation === 'horizontal';

  const navStyle = isHorizontal
    ? 'px-6 py-2.5 flex items-center gap-2 rounded-lg transition-all duration-200 whitespace-nowrap'
    : 'w-12 h-12 flex items-center justify-center rounded-lg transition-all duration-200';

  const navActive = 'bg-greenPrimary text-white shadow-md scale-105';
  const navHover = 'text-gray-600 hover:bg-gray-100 hover:text-greenPrimary hover:scale-105';
  const navDisabled = 'opacity-30 cursor-not-allowed pointer-events-none';

  const isActiveNav = ({ isActive }) => [navStyle, isActive ? navActive : navHover].join(' ');

  const containerClass = isHorizontal
    ? 'sticky top-0 z-20 flex gap-3 bg-white px-4 py-3 border-b border-gray-200'
    : 'sticky top-0 z-20 flex flex-col gap-3 bg-white px-3 py-4 min-w-[72px]';

  const TabContent = ({ label, icon: Icon, _isActive }) => {
    if (isHorizontal) {
      return (
        <>
          {Icon && <Icon className="w-5 h-5" />}
          <span className="text-sm font-medium">{label}</span>
        </>
      );
    }
    return Icon ? <Icon className="w-6 h-6" /> : null;
  };

  TabContent.propTypes = {
    label: PropTypes.string,
    icon: PropTypes.elementType,
    _isActive: PropTypes.bool
  };

  return (
    <nav className={containerClass}>
      {tabs.map(({ label, to, permission, icon: Icon }) => {
        const hasPermission = !checkPermissions || userPermissions.includes(permission);

        const tabElement = (
          <NavLink
            key={to}
            to={to}
            className={props => `${isActiveNav(props)} ${!hasPermission ? navDisabled : ''}`}
            onClick={e => {
              if (!hasPermission) e.preventDefault();
            }}
          >
            <TabContent label={label} icon={Icon} isActive={false} />
          </NavLink>
        );

        return isHorizontal ? (
          tabElement
        ) : (
          <TooltipComponent key={to} text={label} position="right">
            {tabElement}
          </TooltipComponent>
        );
      })}
    </nav>
  );
};

NavTabs.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.object),
  checkPermissions: PropTypes.bool,
  orientation: PropTypes.string
};

export default NavTabs;
